// Clinical discipline display metadata — the single source for how a
// discipline is named in the UI.
//
// A discipline is named in three registers, and they are not
// interchangeable. Compact grids have room for a code and nothing else;
// a group heading wants the full name; a sentence wants the name in
// running prose. Before this module each surface kept its own map, and
// they drifted: the Clinician Manager matrix rendered
// `Nursing | PT | OT | ST | MSW | HHA` — the one entry that had been
// spelled out among five abbreviations.

import type { DisciplineType } from '../services/api/models/DisciplineType';

type DisciplineMeta = {
  /** Full name, Title Case, for group and section headings. */
  name: string;
  /** Full name for running prose: "Assignable for Nursing and PT visits". */
  prose: string;
  /** Position in DISCIPLINE_ORDER below: nursing first, then the therapies,
   * then aide and social work. */
  order: number;
};

export const DISCIPLINES = {
  SN: { name: 'Skilled Nursing', prose: 'Nursing', order: 0 },
  PT: { name: 'Physical Therapy', prose: 'Physical therapy', order: 1 },
  OT: { name: 'Occupational Therapy', prose: 'Occupational therapy', order: 2 },
  ST: { name: 'Speech Therapy', prose: 'Speech therapy', order: 3 },
  HHA: { name: 'Home Health Aide', prose: 'Home health aide', order: 4 },
  MSW: { name: 'Medical Social Work', prose: 'Medical social work', order: 5 },
} as const satisfies Record<DisciplineType, DisciplineMeta>;

// Disciplines arrive from the API as plain strings, so every lookup has to
// survive a value outside the union. Each accessor falls back to the raw
// input, which for an unknown discipline is the code itself.
const meta = (discipline: string): DisciplineMeta | undefined =>
  DISCIPLINES[discipline as DisciplineType];

// A discipline's code is just its DisciplineType value (e.g. "SN"), so
// there is no accessor for it — unlike name/prose, it's not a lookup, it's
// the input string itself.

export const disciplineName = (discipline: string): string =>
  meta(discipline)?.name ?? discipline;

export const disciplineProse = (discipline: string): string =>
  meta(discipline)?.prose ?? discipline;

// Governs the clinic-disciplines matrix columns, the goals filter, the
// authorization budget strip, and grouping on Insurance Rates and Scheduling
// Points. Derived from each entry's `order` rather than a second hand-kept
// list.
export const DISCIPLINE_ORDER: readonly DisciplineType[] = (
  Object.keys(DISCIPLINES) as DisciplineType[]
).sort((a, b) => DISCIPLINES[a].order - DISCIPLINES[b].order);

// Sort key: known disciplines by DISCIPLINE_ORDER, unknown ones after.
export const disciplineSortIndex = (discipline: string): number => {
  const idx = DISCIPLINE_ORDER.indexOf(discipline as DisciplineType);
  return idx === -1 ? DISCIPLINE_ORDER.length : idx;
};
